import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCcw, Loader2, ExternalLink, Building2, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("kcis_admin_token");
  return { Authorization: `Bearer ${t}` };
}

const STATUS_PILL = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  denied: "bg-rose-100 text-rose-800",
  more_info_requested: "bg-sky-100 text-sky-800",
};

function DecisionPanel({ appId, onDecided }) {
  const [decision, setDecision] = useState("approved");
  const [note, setNote] = useState("");
  const [bridgeModules, setBridgeModules] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { decision, note: note || null };
      if (bridgeModules.trim()) {
        payload.bridge_modules_assigned = bridgeModules
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      const r = await fetch(
        `${API}/api/admin/kuhas/residency/applications/${appId}/decide`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify(payload),
        }
      );
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail?.message || `HTTP ${r.status}`);
      toast.success(`Application ${body.review_status}`);
      onDecided();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid={`decision-panel-${appId}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs">
          <span className="text-slate-600">Decision</span>
          <select
            value={decision}
            onChange={(e) => setDecision(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm bg-white"
            data-testid={`decision-select-${appId}`}
          >
            <option value="approved">Approve</option>
            <option value="more_info_requested">Request more info</option>
            <option value="denied">Deny</option>
          </select>
        </label>
        <label className="text-xs">
          <span className="text-slate-600">Bridge modules (comma-separated course IDs, optional)</span>
          <input
            value={bridgeModules}
            onChange={(e) => setBridgeModules(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm"
            placeholder="e.g. dts-101,dts-205"
          />
        </label>
      </div>
      <label className="block text-xs">
        <span className="text-slate-600">Note (optional)</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          rows={2}
        />
      </label>
      <button
        onClick={submit}
        disabled={saving}
        className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium h-9 px-4 inline-flex items-center gap-1.5"
        data-testid={`submit-decision-${appId}`}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
        Submit decision
      </button>
    </div>
  );
}

export default function AdminKuhasResidency() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = new URL(`${API}/api/admin/kuhas/residency/applications`);
      if (status) url.searchParams.set("review_status", status);
      const r = await fetch(url.toString(), { headers: authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setItems((await r.json()).items || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-xs text-purple-900 hover:underline flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Admin hub
            </Link>
            <span className="font-display text-lg text-slate-900">CCT Residency · Application Review</span>
          </div>
          <button onClick={load} disabled={loading}
                  className="text-xs text-slate-700 hover:text-slate-900 flex items-center gap-1">
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-4" data-testid="admin-kuhas-residency">
        <div>
          <h1 className="font-display text-3xl text-slate-900">CCT Applications</h1>
          <p className="mt-1 text-sm text-slate-600">
            Externally-trained trichologists applying for the standalone Clinical Residency (CCT).
            Verification is a human admin decision — no AI scoring.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Filter:</span>
          {["pending", "more_info_requested", "approved", "denied", ""].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatus(s)}
              className={`px-2.5 h-7 rounded-full ${
                status === s ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"
              }`}
              data-testid={`filter-${s || "all"}`}
            >
              {s || "all"}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 text-center">
            No applications match this filter.
          </div>
        ) : (
          items.map((a) => (
            <article
              key={a.id}
              className="rounded-2xl border border-slate-200 bg-white p-5"
              data-testid={`app-row-${a.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-900" />
                    <span className="font-display text-lg text-slate-900">{a.applicant_name}</span>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${STATUS_PILL[a.review_status] || "bg-slate-100 text-slate-700"}`}>
                      {a.review_status.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-slate-600 font-mono">{a.applicant_email}</div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-700">
                    <div><span className="text-slate-500">Prior certification:</span> {a.prior_certification_name}</div>
                    <div><span className="text-slate-500">Issuing body:</span> {a.issuing_body}</div>
                    <div><span className="text-slate-500">Hours claimed:</span> {a.hours_claimed}</div>
                    <div>
                      <span className="text-slate-500">Documentation:</span>{" "}
                      <a href={a.documentation_url} target="_blank" rel="noreferrer"
                         className="text-purple-900 hover:underline inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                    </div>
                  </div>
                  {a.decision_note && (
                    <div className="mt-3 text-xs text-slate-600 italic bg-slate-50 rounded-lg p-2">
                      Prior note: {a.decision_note}
                    </div>
                  )}
                </div>
                {a.review_status === "pending" || a.review_status === "more_info_requested" ? (
                  <button
                    onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                    className="rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs h-8 px-3 flex-shrink-0"
                    data-testid={`decide-btn-${a.id}`}
                  >
                    {expanded === a.id ? "Close" : "Decide"}
                  </button>
                ) : (
                  <div className="text-xs text-slate-400 flex-shrink-0">
                    Decided {a.reviewed_at ? new Date(a.reviewed_at).toLocaleDateString() : ""}
                  </div>
                )}
              </div>
              {expanded === a.id && (
                <DecisionPanel appId={a.id} onDecided={() => { setExpanded(null); load(); }} />
              )}
            </article>
          ))
        )}
      </main>
    </div>
  );
}
